import {
    SET_IS_OWNERSHIP_RESULTS_EXPANDED,
    START_OWNERSHIP_SEARCH_REQUEST,
    START_OWNERSHIP_SEARCH_SUCCESS,
    START_OWNERSHIP_SEARCH_FAILURE,
    FETCH_OWNERSHIP_SEARCH_REQUEST,
    FETCH_OWNERSHIP_SEARCH_SUCCESS,
    FETCH_OWNERSHIP_SEARCH_FAILURE,
    PING_OWNERSHIP_SEARCH_REQUEST,
    PING_OWNERSHIP_SEARCH_SUCCESS,
    PING_OWNERSHIP_SEARCH_FAILURE,
    CANCEL_OWNERSHIP_SEARCH_REQUEST,
    CANCEL_OWNERSHIP_SEARCH_SUCCESS,
    CANCEL_OWNERSHIP_SEARCH_FAILURE,
    FETCH_OWNERSHIP_SEARCH_RESULTS_REQUEST,
    FETCH_OWNERSHIP_SEARCH_RESULTS_SUCCESS,
    FETCH_OWNERSHIP_SEARCH_RESULTS_FAILURE
} from './Types';

import axios, { CancelTokenSource } from 'axios';
import _, { debounce } from 'lodash';
import { MAIN_MAP } from '../map/Constants';
import { addDrawFeature, setFitBoundsFromGeojson } from '../map/Actions';
import { setAreaCriteria, setAreaLayer } from '../search_area/Actions';
import { OWNERSHIP_REDUCER, SEARCH_REDUCER } from '../ReducerNames';
import { Criteria, Geometry, LayerData, Search } from '../../utility/Types/commonTypes';
import { OwnershipResults } from './ownershipTypes';

const CancelToken = axios.CancelToken;
let startSearchSource: CancelTokenSource, fetchSearchSource: CancelTokenSource, pingSearchSource: CancelTokenSource | null;

export const setIsOwnershipResultsExpanded = (isExpanded: boolean) => ({ type: SET_IS_OWNERSHIP_RESULTS_EXPANDED, isExpanded});
const startSearch = () => ({ type: START_OWNERSHIP_SEARCH_REQUEST });
const startSearchSuccess = (search: Search) => ({ type: START_OWNERSHIP_SEARCH_SUCCESS, search });
const startSearchError = (error: any) => ({ type: START_OWNERSHIP_SEARCH_FAILURE, error });
const fetchSearch = (id?: number) => ({ type: FETCH_OWNERSHIP_SEARCH_REQUEST, id });
export const fetchSearchSuccess = (search: Search) => ({ type: FETCH_OWNERSHIP_SEARCH_SUCCESS, search });
const fetchSearchError = (id: number, error?: any) => ({ type: FETCH_OWNERSHIP_SEARCH_FAILURE, id, error });
const pingSearch = () => ({ type: PING_OWNERSHIP_SEARCH_REQUEST });
const pingSearchSuccess = (search: Search) => ({ type: PING_OWNERSHIP_SEARCH_SUCCESS, search });
const pingSearchError = (error: any) => ({ type: PING_OWNERSHIP_SEARCH_FAILURE, error });
const cancelSearch = () => ({ type: CANCEL_OWNERSHIP_SEARCH_REQUEST });
const cancelSearchSuccess = () => ({ type: CANCEL_OWNERSHIP_SEARCH_SUCCESS });
const cancelSearchError = (error: any) => ({ type: CANCEL_OWNERSHIP_SEARCH_FAILURE, error });

export const setSearchAreaCriteria = (criteria: Criteria) => {
    return (dispatch: (arg0: { type: string; reducerName: string; criteria: Criteria; }) => void) => {
        dispatch(setAreaCriteria(OWNERSHIP_REDUCER, criteria));
    };
};

export const setSearchLayerId = (id: number) => {
    return (dispatch: (arg0: (dispatch: (dispatch: any) => Promise<void>, getState: () => any) => void) => void) => {
        dispatch(setAreaLayer(OWNERSHIP_REDUCER, id));
    };
};

const ping = (dispatch: (arg0: { type: string; search?: Search; error?: any; }) => void, id: number) => {
    pingSearchSource && pingSearchSource.cancel('Cancelling already running request');
    dispatch(pingSearch());
    return axios.get(`/api/searches/${id}`, {
        cancelToken: (pingSearchSource = axios.CancelToken.source()).token
    }).then(response => {
        pingSearchSource = null;
        dispatch(pingSearchSuccess(response.data));
        // If still running then ping again
        if (response.data.status === 'ready' || response.data.status === 'queued' || response.data.status === 'running') {
            debouncedPing(dispatch, response.data.id);
        } 
        // else if (response.data.status === 'complete') {
        //     dispatch(fetchSearchResultsAsync(id, 0));
        // }
    }).catch(error => {
        pingSearchSource = null;
        if (axios.isCancel(error)) {
            console.log('cancel', error);
        } else {
            dispatch(pingSearchError(error.response));
            console.error(error);
        }
    });
};

const debouncedPing = debounce(ping, 1000);

export const startSearchAsync = (projectId: number, layer: LayerData, areaFacetId: number, areaFacetValue: any, geometry: Geometry, criteria: Criteria) => {
    return (dispatch: (arg0: { type: string; search?: Search | undefined; error?: any; }) => void) => {
        cancelRequests();

        dispatch(startSearch());
        return axios.post(`/api/projects/${projectId}/searches`, {
            layer: layer,
            area_facet_id: areaFacetId,
            area_facet_value: areaFacetValue,
            geometry: geometry,
            criteria: criteria
        }, {
            cancelToken: (startSearchSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(startSearchSuccess(response.data));
            debouncedPing(dispatch, response.data.id);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(startSearchError(error.response));
                console.error(error);
            }
        });
    };
};

// This function assumes that once the search loads the user wants to go to and show on the map
export const fetchSearchAsync = (id: number) => {
    return (dispatch: any) => {
        cancelRequests();

        dispatch(fetchSearch());
        return axios.get(`/api/searches/${id}`, {
            cancelToken: (fetchSearchSource = axios.CancelToken.source()).token
        }).then(response => {
            if (response.data.areaFacet.layerType === 'drawn') { 
                dispatch(addDrawFeature(_.cloneDeep(response.data.area), MAIN_MAP)); 
            }
            if (response.data.bounds) {
                dispatch(setFitBoundsFromGeojson(response.data.bounds, { maxZoom: 16 }, MAIN_MAP));
            }
            dispatch(fetchSearchSuccess(response.data));
            if (['ready', 'queued', 'running'].includes(response.data.status)) {
                debouncedPing(dispatch, response.data.id);
            } 
            // else if (response.data.status === 'complete') {
            //     dispatch(fetchSearchResultsAsync(id, 0));
            // }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(fetchSearchError(error.response));
                console.error(error);
            }
        });
    };
};

export const cancelSearchAsync = (id: number) => {
    return (dispatch: (arg0: { type: string; error?: any; }) => void) => {
        cancelRequests();
        dispatch(cancelSearch());
        return axios.patch(`/api/searches/${id}/cancel`, {

        }).then(response => {
            dispatch(cancelSearchSuccess());
        }).catch(error => {
            dispatch(cancelSearchError(error.response));
            console.error(error.response);
        });
    };
};

const cancelRequests = () => {
    debouncedPing.cancel();
    pingSearchSource && pingSearchSource.cancel('Operation cancelled by user');
    startSearchSource && startSearchSource.cancel('Cancelling already running request');
    fetchSearchSource && fetchSearchSource.cancel('Cancelling already running request');
};

const fetchSearchResults = (id?: number, start?: number, end?: number) => ({ type: FETCH_OWNERSHIP_SEARCH_RESULTS_REQUEST, id, start, end });
const fetchSearchResultsSuccess = (id: number, start: number, end: number, results: OwnershipResults) => ({ type: FETCH_OWNERSHIP_SEARCH_RESULTS_SUCCESS, id, results, start, end });
const fetchSearchResultsError = (id: number, error: any) => ({ type: FETCH_OWNERSHIP_SEARCH_RESULTS_FAILURE, id, error });
// This function assumes that once the search loads the user wants to go to and show on the map
export const fetchSearchResultsAsync = (id: number, start: number, end: number) => {
    return (dispatch: (arg0: { type: string; id?: number; start?: number; end?: number; results?: OwnershipResults; error?: any; }) => void) => {
        dispatch(fetchSearchResults());
        return axios.get(`/api/searches/${id}/results?start_index=${start}&end_index=${end}`, {
            // cancelToken: (fetchSearchSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(fetchSearchResultsSuccess(id, start, end, response.data));    
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(fetchSearchResultsError(id, error.response));
                console.error(error);
            }
        });
    };
};